export const userTypeValues = ["admin", "agent", "client"] as const;
export type UserType = (typeof userTypeValues)[number];

export const roleAudienceValues = ["internal", "client", "all"] as const;
export type RoleAudience = (typeof roleAudienceValues)[number];

export const permissionGroups = [
  {
    key: "administration",
    label: "Administration",
    permissions: [
      "users.read",
      "users.create",
      "users.update",
      "users.deactivate",
      "users.invite",
      "roles.read",
      "roles.create",
      "roles.update",
      "roles.delete",
      "audit_logs.read",
      "settings.read",
      "settings.update",
      "email_templates.read",
      "email_templates.update",
      "email_templates.send_test",
    ],
  },
  {
    key: "commercial",
    label: "Commercial",
    permissions: [
      "clients.read",
      "clients.create",
      "clients.update",
      "clients.delete",
      "contacts.read",
      "contacts.create",
      "contacts.update",
      "contacts.delete",
      "contracts.read",
      "contracts.create",
      "contracts.update",
      "contracts.delete",
      "budgets.read",
      "budgets.create",
      "budgets.update",
      "budgets.delete",
      "service_types.read",
      "service_types.create",
      "service_types.update",
      "service_types.delete",
      "service_items.read",
      "service_items.create",
      "service_items.update",
      "service_items.delete",
    ],
  },
  {
    key: "billing",
    label: "Billing",
    permissions: [
      "products.read",
      "products.create",
      "products.update",
      "products.delete",
      "services.read",
      "services.create",
      "services.update",
      "services.delete",
      "renewals.read",
      "renewals.update",
      "email_logs.read",
      "invoices.read",
      "invoices.create",
      "invoices.update",
      "invoices.delete",
      "invoices.push_xero",
      "reporting.read",
    ],
  },
  {
    key: "support",
    label: "Support",
    permissions: [
      "tickets.read",
      "tickets.create",
      "tickets.update",
      "tickets.delete",
      "tickets.reply",
      "tickets.assign",
      "tickets.capture_payment",
      "tickets.release_payment",
      "channels.read",
      "channels.create",
      "channels.update",
      "channels.delete",
      "canned_replies.read",
      "canned_replies.create",
      "canned_replies.update",
      "canned_replies.delete",
    ],
  },
  {
    key: "intake",
    label: "Intake",
    permissions: [
      "work_requests.read",
      "work_requests.update",
      "work_requests.delete",
      "knowledgebase.read",
      "knowledgebase.create",
      "knowledgebase.update",
      "knowledgebase.delete",
      "ai_scoping_logs.read",
    ],
  },
  {
    key: "delivery",
    label: "Delivery",
    permissions: [
      "projects.read",
      "projects.create",
      "projects.update",
      "projects.delete",
      "project_tasks.read",
      "project_tasks.create",
      "project_tasks.update",
      "project_tasks.delete",
      "bookings.read",
      "bookings.create",
      "bookings.update",
      "bookings.delete",
      "capacity.read",
      "staff_profiles.read",
      "staff_profiles.update",
    ],
  },
  {
    key: "integrations",
    label: "Integrations",
    permissions: ["integrations.read", "integrations.update"],
  },
  {
    key: "site_audits",
    label: "Site Audits",
    permissions: ["site_audits.read", "site_audits.create", "site_audits.delete"],
  },
  {
    key: "files",
    label: "Client Files",
    permissions: ["files.read", "files.manage"],
  },
  {
    key: "portal",
    label: "Client Portal",
    permissions: [
      "portal.dashboard.read",
      "portal.account.read",
      "portal.account.update",
      "portal.services.read",
      "portal.projects.read",
      "portal.tasks.read",
      "portal.tickets.read",
      "portal.tickets.create",
      "portal.tickets.reply",
      "portal.invoices.read",
      "portal.requests.create",
      "portal.files.read",
      "portal.files.upload",
      "portal.partners.manage",
    ],
  },
] as const;

export const permissionValues = [
  "*",
  ...permissionGroups.flatMap((group) => group.permissions),
] as const;

export type Permission = (typeof permissionValues)[number];

export interface PermissionDefinition {
  key: Permission;
  group: (typeof permissionGroups)[number]["key"];
  label: string;
}

export const permissionCatalog: PermissionDefinition[] = permissionGroups.flatMap(
  (group) =>
    group.permissions.map((permission) => ({
      key: permission,
      group: group.key,
      label: permission,
    })),
);

export interface SeedRoleDefinition {
  slug: string;
  name: string;
  description: string;
  audience: RoleAudience;
  permissions: Permission[];
}

export const systemRoleDefinitions: SeedRoleDefinition[] = [
  {
    slug: "admin",
    name: "Admin",
    description: "Full access to every area of the platform.",
    audience: "internal",
    permissions: ["*"],
  },
  {
    slug: "client",
    name: "Client",
    description: "Portal access for linked client contacts.",
    audience: "client",
    permissions: permissionGroups.find((group) => group.key === "portal")!
      .permissions as unknown as Permission[],
  },
  {
    slug: "client-partner",
    name: "Client Partner",
    description: "Upload-only supplier access to a single folder under a client.",
    audience: "client",
    permissions: ["portal.files.read", "portal.files.upload"],
  },
  {
    slug: "support-agent",
    name: "Support Agent",
    description: "Manage tickets without broader admin or settings access.",
    audience: "internal",
    permissions: [
      "tickets.read",
      "tickets.create",
      "tickets.update",
      "tickets.delete",
      "tickets.reply",
      "tickets.assign",
      "canned_replies.read",
    ],
  },
  {
    slug: "project-user",
    name: "Project User",
    description: "Manage projects and project tasks.",
    audience: "internal",
    permissions: [
      "projects.read",
      "projects.create",
      "projects.update",
      "projects.delete",
      "project_tasks.read",
      "project_tasks.create",
      "project_tasks.update",
      "project_tasks.delete",
    ],
  },
  {
    slug: "account-manager",
    name: "Account Manager",
    description: "Manage client-side commercial records and service delivery.",
    audience: "internal",
    permissions: [
      "clients.read",
      "clients.create",
      "clients.update",
      "clients.delete",
      "contacts.read",
      "contacts.create",
      "contacts.update",
      "contacts.delete",
      "contracts.read",
      "contracts.create",
      "contracts.update",
      "contracts.delete",
      "budgets.read",
      "budgets.create",
      "budgets.update",
      "budgets.delete",
      "service_items.read",
      "service_items.create",
      "service_items.update",
      "service_items.delete",
      "service_types.read",
      "products.read",
      "services.read",
      "services.create",
      "services.update",
      "services.delete",
      "renewals.read",
      "email_logs.read",
      "invoices.read",
      "reporting.read",
      "files.read",
      "files.manage",
    ],
  },
  {
    slug: "site-audit-analyst",
    name: "Site Audit Analyst",
    description: "Run and view site audit reports for prospects.",
    audience: "internal",
    permissions: ["site_audits.read", "site_audits.create", "site_audits.delete"],
  },
  {
    slug: "finance",
    name: "Finance",
    description: "Manage commercial and billing operations, including Xero push.",
    audience: "internal",
    permissions: [
      "clients.read",
      "contacts.read",
      "contracts.read",
      "budgets.read",
      "service_types.read",
      "service_types.create",
      "service_types.update",
      "service_types.delete",
      "service_items.read",
      "service_items.create",
      "service_items.update",
      "service_items.delete",
      "products.read",
      "products.create",
      "products.update",
      "products.delete",
      "services.read",
      "services.create",
      "services.update",
      "services.delete",
      "renewals.read",
      "renewals.update",
      "email_logs.read",
      "invoices.read",
      "invoices.create",
      "invoices.update",
      "invoices.delete",
      "invoices.push_xero",
      "reporting.read",
    ],
  },
];

export const systemRoleSlugs = systemRoleDefinitions.map((role) => role.slug);

const legacyRoleAliases = {
  super_admin: "admin",
  admin: "admin",
  agent: "support-agent",
  client: "client",
} as const;

const legacyPermissionAliases: Record<string, Permission> = {
  "contracts.manage": "contracts.update",
  "commercial.manage": "clients.update",
  "work.manage": "projects.update",
  "projects.manage": "projects.update",
  "projects.read": "projects.read",
  "scheduling.manage": "bookings.update",
  "scheduling.read": "bookings.read",
  "finance.read": "reporting.read",
  "reporting.read": "reporting.read",
  "integrations.manage": "integrations.update",
  "support.manage": "tickets.update",
  "intake.manage": "work_requests.update",
  "portal.read": "portal.dashboard.read",
  "portal.projects.read": "portal.projects.read",
  "portal.tasks.read": "portal.tasks.read",
  "portal.invoices.read": "portal.invoices.read",
};

export function isValidPermission(value: string): value is Permission {
  return (permissionValues as readonly string[]).includes(value);
}

export function getPermissionGroup(permission: Permission) {
  return permissionCatalog.find((item) => item.key === permission)?.group ?? null;
}

export function hasPermission(
  permissions: readonly Permission[],
  permission: Permission,
): boolean {
  return permissions.includes("*") || permissions.includes(permission);
}

export type ApplicationRole = keyof typeof legacyRoleAliases;

export function getPermissionsForRole(role: ApplicationRole): readonly Permission[] {
  const slug = legacyRoleAliases[role];
  return (
    systemRoleDefinitions.find((definition) => definition.slug === slug)?.permissions ?? []
  );
}

export function roleHasPermission(
  role: ApplicationRole,
  permission: Permission | keyof typeof legacyPermissionAliases,
): boolean {
  const resolvedPermission = (legacyPermissionAliases[permission] ?? permission) as Permission;
  return hasPermission(getPermissionsForRole(role), resolvedPermission);
}
